import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';

import * as inventoryActions from '@inventory/store/inventory.actions';
import * as inventorySelectors from '@inventory/store/inventory.selectors';

import { environment } from '@environment';

import * as pcacheActions from '../store/pcache/pcache.actions';
import * as pcacheSelectors from '../store/pcache/pcache.selectors';
import * as tempdataSelectors from '../store/tempdata/tempdata.selectors';
import { InventoryFilter } from './shared/inventory-filter.model';

@Injectable()
export class InventoryService {
  // OBSERVABLES
  isShowing$ = this.store.select(inventorySelectors.selectInvSumShowing);

  isFilter$ = this.store.select(inventorySelectors.selectInvSumFilter).pipe(distinctUntilChanged());

  isColPref$ = this.store.select(pcacheSelectors.selectInvSumColPref).pipe(
    tap((pref) => {
      if (!pref) {
        this.store.dispatch(new pcacheActions.ReInit());
      }
    }),
    filter((pref) => !!pref)
  );

  isGridData$ = this.store.select(tempdataSelectors.selectInvSumGridData);

  isSelectedRow$ = this.store.select(inventorySelectors.selectInvSumSelectedRow);

  idShowing$ = this.store.select(inventorySelectors.selectInvDtlShowing);

  idGridData$ = this.store.select(tempdataSelectors.selectInvDtlGridData);

  isDtlSelectedRow$ = this.store.select(inventorySelectors.selectInvDtlSelectedRow);

  lotReqValue$ = this.store.select(inventorySelectors.inventoryItemLotRequiredVal)

  ilGridData$ = this.store.select(tempdataSelectors.selectInvLotGridData);

  ilShowing$ = this.store.select(inventorySelectors.selectInvLotShowing);

  idColPref$ = this.store.select(pcacheSelectors.selectInvDtlColPref).pipe(
    tap((pref) => {
      if (!pref) {
        this.store.dispatch(new pcacheActions.ReInit());
      }
    }),
    filter((pref) => !!pref)
  );

  constructor(private http: HttpClient, private store: Store) {}

  setInvSumShowing(area: string) {
    this.store.dispatch(new inventoryActions.InvSumSetShowing(area));
  }

  setInvDtlShowing(area: string) {
    this.store.dispatch(new inventoryActions.InvDtlSetShowing(area));
  }

  setInvLotShowing(area: string) {
    this.store.dispatch(new inventoryActions.InvLotSetShowing(area));
  }

  getInventoryFcpiByFilter(invFilter: InventoryFilter) {
    return this.getInventoryFcpi(
      invFilter.getFacility,
      invFilter.getCustomer,
      invFilter.getProductGroup,
      invFilter.getItem,
      invFilter.getItemFilter,
      invFilter.getIgnoreZeroQty,
      invFilter.getLotFilter,
      invFilter.getLotNumber
    );
  }

  getInventoryFcpi(
    facility: string = null,
    custid: string = null,
    productGroup: string = null,
    item: string = null,
    itemFilter: string = null,
    ignoreZeroQty: string = 'N',
    lotFilter: string = null,
    lotNumber: string = null
  ): Observable<any[]> {
    return this.http
      .post(
        environment.inventoryFcpiEndpoint,
        { facility, custid, productGroup, item, itemFilter, ignoreZeroQty, lotFilter, lotNumber },
        { withCredentials: true }
      )
      .pipe(map((response: any) => response.inventory));
  }

  getInventoryItemDetail(facility: string = null, item: string = null): Observable<any[]> {
    return this.http
      .post(environment.inventoryItemDetailEndpoint, { facility, item }, { withCredentials: true })
      .pipe(map((response: any) => response.inventory));
  }

  getInventoryItemLotRequiredVal(item: string = null): Observable<any[]> {
    return this.http
      .post(environment.inventoryItemLotRequiredVal, { item }, { withCredentials: true })
      .pipe(map((response: any) => response));
  }

  getInventoryItemLot(data: any): Observable<any[]> {
    return this.http
      .post(environment.inventoryItemLotEndpoint, data, { withCredentials: true })
      .pipe(map((response: any) => response.inventory));
  }
}
